import { switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PageConfigStore } from '@/app/features/page-config';
import { SubscriptionPlanEnum } from '@/app/features/subscription';

import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { CdkMenu, CdkMenuTrigger } from '@angular/cdk/menu';
import { input, inject, signal, Signal, OnInit, computed, Component, InputSignal, WritableSignal } from '@angular/core';

import { AuthService } from '@/app/core/services/auth/auth.service';
import { AlertService } from '@/app/core/services/alert/alert.service';

import { AppDialog } from '@/app/shared/components/ui/dialog';
import { SvgIconComponent } from '@/app/shared/components/ui/svg-icon/svg-icon.component';
import { ToggleSwitchComponent } from '@/app/shared/components/ui/toggleswitch/toggle-switch.component';
import { SidebarMenuComponent } from '@/app/shared/components/common/sidebar-menu/sidebar-menu.component';
import { ConfirmModalComponent } from '@/app/shared/components/modals/confirm-modal/confirm-modal.component';
import { EntityAvatarComponent } from '@/app/shared/components/common/entity-avatar/entity-avatar.component';
import { MenuItemInterface } from '@/app/shared/components/common/sidebar-menu/Interfaces/menu-item.interface';
import { ProfileMenuItemInterface } from '@/app/shared/components/layout/sidebar/Interfaces/profile-menu-item.interface';
import { BecomeResellerModalComponent } from '@/app/shared/components/modals/become-reseller-modal/become-reseller-modal.component';

import { UserInterface } from '@/app/shared/interfaces/user.interface';

@Component({
  selector: 'app-sidebar',
  imports: [
    EntityAvatarComponent,
    SidebarMenuComponent,
    CdkMenu,
    CdkMenuTrigger,
    SvgIconComponent,
    ToggleSwitchComponent,
    FormsModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  host: {
    '[class.open]': 'isHamburgerOpen()',
  },
})
export class SidebarComponent implements OnInit {
  private readonly authService: AuthService = inject(AuthService);

  private readonly router: Router = inject(Router);

  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly appDialog: AppDialog = inject(AppDialog);

  private readonly alertService: AlertService = inject(AlertService);

  protected readonly pageConfigStore: PageConfigStore = inject(PageConfigStore);

  public isAdminLayout: InputSignal<boolean> = input<boolean>(false);

  public editedUser: InputSignal<UserInterface | undefined> = input<UserInterface | undefined>(undefined);

  user: WritableSignal<UserInterface | null> = signal<UserInterface | null>(this.authService.user());

  protected isHamburgerOpen: WritableSignal<boolean> = signal(false);

  readonly menuPosition = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetX: 12,
    },
  ];

  protected profileMenu: Signal<ProfileMenuItemInterface[]> = computed(() => {
    const menu = [
      {
        title: this.translateService.instant('components.sidebar.profile-menu.become-reseller.label'),
        icon: 'become-reseller',
        border: true,
        key: 'become-reseller',
        visible: !this.user()?.admin_type,
      },
      {
        title: this.translateService.instant('components.sidebar.profile-menu.upgrade-subscription.label'),
        icon: 'upgrade-subscription',
        border: true,
        key: 'upgrade-subscription',
        visible:
          this.user()?.plan?.type !== SubscriptionPlanEnum.Premium || this.user()?.plan?.source_type !== 'subscription',
      },
      {
        title: this.translateService.instant('components.sidebar.profile-menu.settings.label'),
        icon: 'settings',
        key: 'settings',
        visible: true,
      },
      {
        title: this.translateService.instant('components.sidebar.profile-menu.log-out.label'),
        icon: 'log-out',
        key: 'log-out',
        visible: true,
      },
    ];

    const switchToUserItem = {
      title: this.translateService.instant('components.sidebar.profile-menu.switch-user.label'),
      icon: 'switch',
      border: true,
      key: 'switch-user',
      visible: !!this.user()?.registered_as_user,
    };

    const switchToAdminItem = {
      title: this.translateService.instant('components.sidebar.profile-menu.switch-admin.label'),
      icon: 'switch',
      border: true,
      key: 'switch-admin',
      visible: !!this.user()?.registered_as_admin,
    };

    menu.unshift(this.isAdminLayout() ? switchToUserItem : switchToAdminItem);

    return menu;
  });

  protected sidebarTopMenu: Signal<MenuItemInterface[]> = computed(() => {
    if (this.isAdminLayout()) {
      return [
        {
          title: this.translateService.instant('components.sidebar.top-menu.admin-layout.analytics.label'),
          link: '/admin/analytics',
          icon: 'analytics',
        },
        {
          title: this.translateService.instant('components.sidebar.top-menu.admin-layout.users.label'),
          link: '/admin/users',
          icon: 'users',
        },
      ];
    }

    return [
      {
        title: this.translateService.instant('components.sidebar.top-menu.user-layout.design.label'),
        link: 'design',
        icon: 'design',
      },
      {
        title: this.translateService.instant('components.sidebar.top-menu.user-layout.redirect.label'),
        link: 'redirect',
        icon: 'redirect',
        key: 'redirect',
      },
      {
        title: this.translateService.instant('components.sidebar.top-menu.user-layout.analytics.label'),
        link: 'analytics',
        icon: 'analytics',
      },
      {
        title: this.translateService.instant('components.sidebar.top-menu.user-layout.products.label'),
        link: 'products',
        icon: 'products',
      },
    ].map((item) => {
      if (!!this.editedUser()) {
        item.link = `admin/users/${this.editedUser()!.id}/${item.link}`;
      }

      return item;
    });
  });

  protected sidebarBottomMenu: Signal<MenuItemInterface[]> = computed(() => {
    if (this.isAdminLayout()) {
      return [
        {
          title: this.translateService.instant('components.sidebar.bottom-menu.admin-layout.branding.label'),
          link: 'admin/branding',
          icon: 'branding',
        },
        {
          title: this.translateService.instant('components.sidebar.bottom-menu.admin-layout.settings.label'),
          link: 'admin/settings',
          icon: 'settings',
        },
      ];
    }

    if (!!this.editedUser()) {
      return [
        {
          title: this.translateService.instant('components.sidebar.bottom-menu.admin-layout.affiliate.label'),
          link: `admin/users/${this.editedUser()!.id}/affiliate-link`,
          icon: 'affiliate',
        },
      ];
    }

    return [
      // {
      //   title: this.translateService.instant('components.sidebar.bottom-menu.user-layout.subscription.label'),
      //   link: 'subscription',
      //   icon: 'subscription',
      // },
      {
        title: this.translateService.instant('components.sidebar.bottom-menu.user-layout.affiliate-link.label'),
        link: 'affiliate-link',
        icon: 'affiliate',
      },
      {
        title: this.translateService.instant('components.sidebar.bottom-menu.user-layout.settings.label'),
        link: 'settings',
        icon: 'settings',
      },
    ];
  });

  protected hasRedirect: boolean = false;

  protected pageConfigObservable = toObservable(this.pageConfigStore.pageConfig);

  ngOnInit(): void {
    this.pageConfigStore.loadPageConfig(this.userId, !!this.editedUser()).subscribe(() => {
      this.hasRedirect = !!this.pageConfigStore.pageConfig()?.redirect_enabled;
    });

    this.pageConfigObservable.subscribe(() => {
      this.hasRedirect = !!this.pageConfigStore.pageConfig()?.redirect_enabled;
    });
  }

  private get userId() {
    return !this.editedUser() ? this.user()?.id : this.editedUser()!.id;
  }

  clickOnProfileMenu(menuItem: ProfileMenuItemInterface) {
    switch (menuItem.key) {
      case 'become-reseller':
        this.becomeReseller();
        break;
      case 'switch-admin':
        this.router.navigate(['/admin']);
        break;
      case 'switch-user':
        this.router.navigate(['/']);
        break;
      case 'settings':
        this.router.navigate(['/settings']);
        break;
      case 'log-out':
        this.authService.logout();
        break;
      default:
        break;
    }
  }

  private becomeReseller() {
    this.appDialog.open(BecomeResellerModalComponent);
  }

  protected clearRedirect() {
    const dialogRef = this.appDialog.open(ConfirmModalComponent, {
      data: {
        title: this.translateService.instant('shared.sidebar.redirect.clear.confirm.title'),
        text: this.translateService.instant('shared.sidebar.redirect.clear.confirm.text'),
        applyLabel: this.translateService.instant('shared.sidebar.redirect.clear.confirm.apply.label'),
      },
    });

    dialogRef.submitted
      .pipe(
        switchMap(() => {
          return this.pageConfigStore.updatePageConfig(
            {
              redirect_enabled: false,
            },
            this.userId,
            !!this.editedUser(),
          );
        }),
      )
      .subscribe((response) => {
        this.alertService.success(this.translateService.instant('shared.sidebar.redirect.clear.success.text'));
      });
  }
}
